const M = 111320;
const rad = (d) => (d * Math.PI) / 180;
const OVERPASS = "https://overpass-api.de/api/interpreter";

async function fetchRaceways(lat, lon) {
  const q = `[out:json][timeout:25];way(around:1500,${lat},${lon})["highway"="raceway"];out geom;`;
  const r = await fetch(OVERPASS, { method: "POST", body: new URLSearchParams({ data: q }), signal: AbortSignal.timeout(15000) });
  if (!r.ok) throw new Error("overpass");
  const j = await r.json();
  return j.elements.filter((e) => e.geometry?.length > 1).map((e) => e.geometry.map((g) => [g.lat, g.lon]));
}

function toLocal(ways, lat0, lon0) {
  const kLon = M * Math.cos(rad(lat0));
  return ways.map((w) => {
    const out = [];
    w.forEach(([la, lo], i) => {
      const e = (lo - lon0) * kLon, n = (la - lat0) * M;
      if (i > 0) {
        const [pe, pn] = out[out.length - 1];
        const steps = Math.floor(Math.hypot(e - pe, n - pn) / 3);
        for (let s = 1; s < steps; s++) out.push([pe + ((e - pe) * s) / steps, pn + ((n - pn) * s) / steps]);
      }
      out.push([e, n]);
    });
    return out;
  });
}

function buildGrid(pts, cell) {
  const map = new Map();
  for (const p of pts) {
    const k = `${Math.floor(p[0] / cell)},${Math.floor(p[1] / cell)}`;
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(p);
  }
  return { cell, map };
}

function nearest({ cell, map }, x, y) {
  const cx = Math.floor(x / cell), cy = Math.floor(y / cell);
  let best = Infinity, bp = null;
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (const p of map.get(`${cx + dx},${cy + dy}`) || []) {
        const d = (p[0] - x) ** 2 + (p[1] - y) ** 2;
        if (d < best) { best = d; bp = p; }
      }
    }
  }
  return [bp, Math.sqrt(best)];
}

const apply = ({ th, s, tx, ty }, x, y) => [s * (x * Math.cos(th) - y * Math.sin(th)) + tx, s * (x * Math.sin(th) + y * Math.cos(th)) + ty];

function score(src, fine, st) {
  return src.reduce((acc, [x, y]) => acc + Math.min(10, nearest(fine, ...apply(st, x, y))[1]), 0) / src.length;
}

function icp(src, coarse, fine, start, iters) {
  let st = { th: start.th, s: start.s, tx: start.tx, ty: start.ty };
  for (let it = 0; it < iters; it++) {
    const grid = it < iters / 2 ? coarse : fine;
    const P = [], Q = [];
    for (const [x, y] of src) {
      const [p, d] = nearest(grid, ...apply(st, x, y));
      if (p && d < grid.cell) { P.push([x, y]); Q.push(p); }
    }
    if (P.length < src.length * 0.3) break;
    const mean = (A, k) => A.reduce((s, p) => s + p[k], 0) / A.length;
    const mp = [mean(P, 0), mean(P, 1)], mq = [mean(Q, 0), mean(Q, 1)];
    let a = 0, b = 0, vp = 0;
    P.forEach((p, i) => {
      const px = p[0] - mp[0], py = p[1] - mp[1], qx = Q[i][0] - mq[0], qy = Q[i][1] - mq[1];
      a += px * qx + py * qy; b += px * qy - py * qx; vp += px * px + py * py;
    });
    const th = Math.max(-0.1, Math.min(0.1, Math.atan2(b, a))), s = Math.min(1.03, Math.max(0.97, Math.hypot(a, b) / vp));
    const [rx, ry] = apply({ th, s, tx: 0, ty: 0 }, mp[0], mp[1]);
    st = { th, s, tx: mq[0] - rx, ty: mq[1] - ry };
  }
  return { ...st, err: score(src, fine, st) };
}

const bboxCenter = (pts) => {
  if (!pts.length) return null;
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  return [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2];
};

export async function autoAlign(lap, track) {
  const { lat: lat0, lon: lon0 } = track;
  const ways = toLocal(await fetchRaceways(lat0, lon0), lat0, lon0);
  if (!ways.length) return null;
  const all = ways.flat();
  const coarse = buildGrid(all, 40), fine = buildGrid(all, 10);
  const [cx, cy] = bboxCenter(lap.x.map((x, i) => [x, lap.y[i]]));
  const step = Math.max(1, Math.floor(lap.x.length / 120));
  const base = lap.x.filter((_, i) => i % step === 0).map((x, i) => [x - cx, lap.y[i * step] - cy]);
  const centers = [bboxCenter(all.filter((p) => Math.hypot(...p) < 500)) || [0, 0], ...ways.filter((w) => w.length > 50).map(bboxCenter)].slice(0, 8);
  let best = null;
  const starts = centers.flatMap(([tx, ty]) => [-60, -30, 0, 30, 60].flatMap((ox) => [-60, -30, 0, 30, 60].map((oy) => ({ th: 0, s: 1, tx: tx + ox, ty: ty + oy }))));
  const quick = starts.map((st) => icp(base, coarse, fine, st, 8)).sort((p, q) => p.err - q.err).slice(0, 4);
  for (const q of quick) {
    const r = icp(base, coarse, fine, q, 30);
    if (!best || r.err < best.err) best = { ...r, flip: false };
  }
  const deg = ((((best.th * 180) / Math.PI) % 360) + 540) % 360 - 180;
  return {
    err: best.err,
    align: { lat: lat0 + best.ty / M, lon: lon0 + best.tx / (M * Math.cos(rad(lat0))), rotation: Math.round(deg * 10) / 10, scale: Number(best.s.toFixed(3)), flip: best.flip },
  };
}
